import React from 'react';
import PropTypes from 'prop-types';
import './LevelBar.scss';

/**
 * Design System LevelBar Component
 *
 * A gaming-inspired progress bar showing XP/Level progression.
 * Perfect for displaying user levels and experience points.
 *
 * @example
 * <LevelBar
 *   currentLevel={12}
 *   currentXP={750}
 *   nextLevelXP={1000}
 *   showLabels={true}
 * />
 */
const LevelBar = ({
    currentLevel,
    currentXP = 0,
    nextLevelXP = 1000,
    showLabels = true,
    showPercentage = false,
    animated = true,
    size = 'medium',
    color = '#FED230',
    backgroundColor = '#F5F5F5',
    className = '',
    style = {},
    ...props
}) => {
    const [displayXP, setDisplayXP] = React.useState(animated ? 0 : currentXP);

    React.useEffect(() => {
        if (animated) {
            const duration = 1200; // 1.2 seconds animation
            const steps = 60;
            const increment = currentXP / steps;
            let current = 0;

            const timer = setInterval(() => {
                current += increment;
                if (current >= currentXP) {
                    setDisplayXP(currentXP);
                    clearInterval(timer);
                } else {
                    setDisplayXP(Math.floor(current));
                }
            }, duration / steps);

            return () => clearInterval(timer);
        } else {
            setDisplayXP(currentXP);
        }
    }, [currentXP, animated]);

    const percentage = Math.min((displayXP / nextLevelXP) * 100, 100);

    const baseClassName = 'ds-level-bar';
    const sizeClassName = `${baseClassName}--${size}`;
    const combinedClassName = [baseClassName, sizeClassName, className].filter(Boolean).join(' ');

    return (
        <div className={combinedClassName} style={style} {...props}>
            {showLabels && (
                <div className="ds-level-bar__header">
                    <div className="ds-level-bar__level">
                        <span className="ds-level-bar__level-label">LEVEL</span>
                        <span className="ds-level-bar__level-number">{currentLevel}</span>
                    </div>

                    <div className="ds-level-bar__xp-info">
                        {showPercentage ? (
                            <span className="ds-level-bar__percentage">{Math.round(percentage)}%</span>
                        ) : (
                            <>
                                <span className="ds-level-bar__current-xp">{displayXP.toLocaleString()}</span>
                                <span className="ds-level-bar__separator">/</span>
                                <span className="ds-level-bar__next-xp">{nextLevelXP.toLocaleString()} XP</span>
                            </>
                        )}
                    </div>
                </div>
            )}

            <div className="ds-level-bar__container">
                <div
                    className="ds-level-bar__background"
                    style={{ backgroundColor }}
                />
                <div
                    className="ds-level-bar__fill"
                    style={{
                        width: `${percentage}%`,
                        backgroundColor: color
                    }}
                >
                    <div className="ds-level-bar__shine" />
                </div>
            </div>

            {showLabels && (
                <div className="ds-level-bar__next-level">
                    Next: Level {currentLevel + 1}
                </div>
            )}
        </div>
    );
};

LevelBar.propTypes = {
    /** Current user level */
    currentLevel: PropTypes.number.isRequired,

    /** Current XP points */
    currentXP: PropTypes.number,

    /** XP needed for next level */
    nextLevelXP: PropTypes.number,

    /** Show level and XP labels */
    showLabels: PropTypes.bool,

    /** Show percentage instead of XP numbers */
    showPercentage: PropTypes.bool,

    /** Animate the progress bar on mount */
    animated: PropTypes.bool,

    /** Bar size */
    size: PropTypes.oneOf(['small', 'medium', 'large']),

    /** Progress bar fill color */
    color: PropTypes.string,

    /** Progress bar background color */
    backgroundColor: PropTypes.string,

    /** Additional CSS classes */
    className: PropTypes.string,

    /** Inline styles */
    style: PropTypes.object
};

export default LevelBar;
